import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, PackagePlus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

const EMPTY = { name: "", part_number: "", quantity: 1, supplier_name: "" };
const FIELDS = [["name", "partName", "auto", true], ["part_number", "partNumber", "ltr", false], ["supplier_name", "supplier", "auto", false]];

export const WarehouseAddForm = ({ onDone }) => {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [dupes, setDupes] = useState([]);
  const [busy, setBusy] = useState(false);

  const save = async (merge_into) => {
    setBusy(true);
    try {
      await api.post("/warehouse", { ...form, quantity: Number(form.quantity), merge_into: merge_into || null });
      toast.success(t("success")); setForm(EMPTY); setDupes([]); onDone();
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.part_number.trim()) {
      const { data } = await api.get("/warehouse/match", { params: { numbers: form.part_number } });
      if (data.length) return setDupes(data);
    }
    save();
  };

  return (
    <form onSubmit={submit} className="bg-white border rounded-lg p-5 space-y-4" data-testid="wh-add-form">
      <h3 className="font-bold flex items-center gap-2"><PackagePlus className="w-4 h-4" />{t("whAdd")}</h3>
      {FIELDS.map(([k, l, dir, req]) => (
        <div key={k} className="space-y-1.5"><Label>{t(l)}</Label>
          <Input dir={dir} data-testid={`wh-${k}-input`} value={form[k]} onChange={(e) => { setForm({ ...form, [k]: e.target.value }); setDupes([]); }} required={req} /></div>
      ))}
      <div className="space-y-1.5"><Label>{t("quantity")}</Label>
        <Input type="number" min={1} data-testid="wh-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required /></div>
      {dupes.length > 0 ? (
        <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="wh-dupe-alert">
          <div className="flex items-center gap-2 font-bold text-amber-900 text-sm"><AlertTriangle className="w-4 h-4" />{t("whDupeTitle")}</div>
          {dupes.map((d) => (
            <div key={d.id} className="bg-white border rounded-md p-2 text-sm flex flex-wrap items-center justify-between gap-2">
              <span><b>{d.name}</b> <span className="font-mono text-xs" dir="ltr">#{d.part_number}</span> · {t("quantity")}: <b>{d.quantity}</b></span>
              <Button type="button" size="sm" disabled={busy} data-testid={`wh-merge-${d.id}`} onClick={() => save(d.id)}>{t("whMerge")}</Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="w-full" disabled={busy} data-testid="wh-dupe-ignore" onClick={() => save()}>{t("whIgnoreAddNew")}</Button>
        </div>
      ) : (
        <Button type="submit" className="w-full" disabled={busy} data-testid="wh-add-submit">{t("add")}</Button>
      )}
    </form>
  );
};
